import { cloneDeep } from 'lodash'

import { isEmpty, proxy } from '@/utils'

import render from './render'

const createDefaultItem = () => {
  return {
    size: undefined,
    price: undefined,
    priority: 0
  }
}

export default {
  name: 'PacketsDialog',

  mixins: [ proxy() ],

  props: {
    data: {
      type: Array,
      default: () => {
        return []
      }
    }
  },

  data() {
    return {
      items: []
    }
  },

  computed: {
    filledItems() {
      return this.filterFilledItems(this.items)
    },
    normalizedItems() {
      return this.filledItems.map((item, index) => {
        return this.normalizeItem(item, index)
      }).filter(Boolean)
    },

    hasIncompleteItems() {
      return this.items.some(item => {
        return this.isPartiallyFilledItem(item)
      })
    },
    hasInvalidItems() {
      return this.filledItems.some((item, index) => {
        return !this.normalizeItem(item, index)
      })
    },

    canSave() {
      return !this.hasIncompleteItems && !this.hasInvalidItems
    }
  },

  watch: {
    proxy(value) {
      if (value) {
        this.syncData()
      }
    },

    data: {
      handler() {
        if (!this.proxy) {
          this.syncData()
        }
      },
      deep: true
    }
  },

  mounted() {
    this.syncData()
  },

  methods: {
    syncData() {
      if (Array.isArray(this.data)) {
        this.items = this.getSortedItems(this.data)

        return
      }

      this.items = []
    },

    isPartiallyFilledItem({ size, price }) {
      const hasSize = !isEmpty(size)
      const hasPrice = !isEmpty(price)

      return (hasSize || hasPrice) && !(hasSize && hasPrice)
    },

    filterFilledItems(data) {
      return data.filter(({ size, price }) => {
        return !isEmpty(size) && !isEmpty(price)
      })
    },

    parsePriority(value) {
      if (typeof value === 'number') {
        if (Number.isSafeInteger(value) && value >= 0) {
          return value
        }

        return undefined
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim()
        if (!/^\d+$/.test(trimmedValue)) {
          return undefined
        }

        return Number(trimmedValue)
      }

      return undefined
    },

    parseSize(value) {
      if (typeof value === 'number') {
        if (Number.isSafeInteger(value) && value > 0) {
          return value
        }

        return undefined
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim()
        if (!/^[1-9]\d*$/.test(trimmedValue)) {
          return undefined
        }

        return Number(trimmedValue)
      }

      return undefined
    },

    parsePrice(value) {
      if (typeof value === 'number') {
        if (Number.isFinite(value) && value > 0) {
          return value
        }

        return undefined
      }

      if (typeof value === 'string') {
        const normalizedValue = value.trim().replace(',', '.')
        if (normalizedValue === '') {
          return undefined
        }

        const parsedValue = Number(normalizedValue)
        if (Number.isFinite(parsedValue) && parsedValue > 0) {
          return parsedValue
        }
      }

      return undefined
    },

    normalizeItem({ size, price, priority }, index) {
      const normalizedSize = this.parseSize(size)
      const normalizedPrice = this.parsePrice(price)
      if (isEmpty(normalizedSize) || isEmpty(normalizedPrice)) {
        return undefined
      }

      let normalizedPriority = this.parsePriority(priority)
      if (isEmpty(normalizedPriority)) {
        normalizedPriority = index
      }

      return {
        size: normalizedSize,
        price: normalizedPrice,
        priority: normalizedPriority
      }
    },

    getSortedItems(data) {
      const items = cloneDeep(data)
      items.sort((firstPacket, secondPacket) => {
        const firstPriority = this.parsePriority(firstPacket.priority)
        const secondPriority = this.parsePriority(secondPacket.priority)

        if (isEmpty(firstPriority) && isEmpty(secondPriority)) {
          return 0
        }
        if (isEmpty(firstPriority)) {
          return 1
        }
        if (isEmpty(secondPriority)) {
          return -1
        }

        return firstPriority - secondPriority
      })

      return items.map((item, index) => {
        return {
          ...item,
          priority: index
        }
      })
    },

    saveHandler() {
      if (this.canSave) {
        this.$emit('save', this.getSortedItems(this.normalizedItems))
        this.proxy = false
      }
    },

    createDefaultItem
  },

  render
}
